import { usePopupContext } from "src/hooks/usePopup";
import { SearchContext } from "src/search/useSearchContext";
import { Field } from "src/search/types";
import { asKey } from "src/utils/fields";
import { attributeToTitle } from "src/utils/strings";
import { serializeParams } from "src/search/lib/params-state-reducer";
import { cloneData } from "src/utils/objects";
import { FilterMenu } from "src/search/components/inputs/FilterMenu";
import { Api } from "@vzbl/integrations";
import { ReactElement } from "react";

/**
 * Hook for handling row interactions in MeasureTable.
 * This is specifically designed for the MeasureTable component and handles:
 * - Row click events
 * - Filter menu popup
 * - Value extraction and formatting
 * - Parameter serialization
 */
interface UseTableRowInteractionProps {
  context: SearchContext;
}

interface TableRowInteraction {
  onRow: (record: Record<string, Api.FieldValue>) => {
    onClick: (e: React.MouseEvent<HTMLElement>) => void;
  };
}

export function useTableRowInteraction({
  context,
}: UseTableRowInteractionProps): TableRowInteraction {
  const popup = usePopupContext();
  const { params } = context;

  const handleRowClick = (
    record: Record<string, Api.FieldValue>,
    e: React.MouseEvent<HTMLElement>
  ): void => {
    const properties = params.state.groupBys || [];

    // Create a clean record mapping with just the group by values
    const recordMapping: Record<string, any> = {};
    const values = properties.map((property) => {
      const key = asKey(property);
      const rawValue = record[key];
      // Extract value from object if needed
      const value =
        rawValue && typeof rawValue === "object" ? rawValue.value : rawValue;
      // Add to record mapping
      recordMapping[key] = value;
      // Return formatted value for display
      return {
        label: String(value || ""),
        value: String(value || ""),
      };
    });

    const serialized = serializeParams(
      params.modifiers.filterDegrouped({
        state: cloneData(params.state),
        record: recordMapping,
        properties,
      })
    );

    const filterMenu: ReactElement = (
      <FilterMenu
        context={context}
        properties={properties}
        values={values}
        measureValue={{
          label: `Filter by ${properties
            .map((p) => attributeToTitle(asKey(p)))
            .join(", ")}`,
          value: serialized,
        }}
      />
    );

    popup.setPopup(e, filterMenu);
  };

  return {
    onRow: (record) => ({
      onClick: (e) => handleRowClick(record, e),
    }),
  };
}
